import { environment } from 'src/environments/environment';
import { InjectionToken, Signal } from '@angular/core';
import type {
  Meter,
  CreateMeterRequest,
  UpdateMeterRequest,
  AddMeterReadingRequest,
  MeterDocument,
  ContractHolder
} from '../models/meter.model';

/**
 * Pattern B (full store) contract for the Meters feature.
 *
 * One interface, two implementations:
 * - `MetersMockStore` — in-memory, for demos / Storybook / specs
 * - `MetersFirestoreStore` — Firestore-backed (wraps the legacy
 *   `MetersService` logic; reads/writes workspace-scoped property subcollections)
 *
 * Components inject `METERS_STORE` instead of a concrete class. The store is
 * **property-scoped**: consumers call `loadForProperty(propertyUID)` once
 * (typically from an `effect()` that tracks the property id) and then read
 * the signals. Swapping properties re-loads; unmounting has no effect.
 *
 * Reads are signal-only. Writes return `Promise<void>` (or the new meter's
 * UID for `createMeter`) so the call site can sequence optimistic UI or
 * navigation.
 */
export interface IMetersStore {
  // --- Raw state ---
  /** Which property the store is currently watching. `null` before first load. */
  readonly currentPropertyUID: Signal<string | null>;

  /** All meters for the current property, sorted by type. */
  readonly meters: Signal<readonly Meter[]>;

  /** `true` while fetching from the backing store. */
  readonly loading: Signal<boolean>;

  /** Last error from a read/write, cleared on successful operation. */
  readonly lastError: Signal<Error | null>;

  // --- Derived state ---
  /** Count of non-deleted meters on the current property. */
  readonly totalCount: Signal<number>;

  /** Meters grouped by type (electricity, gas, water, heating, other). */
  readonly metersByType: Signal<Readonly<Record<string, number>>>;

  /** Count of meters with `status === 'active'`. */
  readonly activeMeters: Signal<number>;

  /** Count of meters where the tenant is the contract holder. */
  readonly tenantMeters: Signal<number>;

  /** Count of meters where the owner is the contract holder. */
  readonly ownerMeters: Signal<number>;

  /**
   * Meters that need attention — no reading in the last 90 days, or non-active
   * status. Useful for dashboard alerts.
   */
  readonly metersNeedingAttention: Signal<{
    readonly noRecentReadings: readonly Meter[];
    readonly inactive: readonly Meter[];
  }>;

  // --- Actions ---
  /**
   * Load meters for a given property. Safe to call repeatedly; a no-op if
   * `propertyUID` matches the one already loaded. Triggers a reload when the
   * id changes.
   */
  loadForProperty(propertyUID: string): void;

  /** Force-refresh the current property's meter list from the backing store. */
  refresh(): Promise<void>;

  /** Create a new meter. Returns the new meter's UID. */
  createMeter(request: CreateMeterRequest): Promise<string>;

  /** Update an existing meter (partial). */
  updateMeter(meterUID: string, updates: UpdateMeterRequest): Promise<void>;

  /** Soft-delete a meter (sets `deleted: true`, filters from list). */
  deleteMeter(meterUID: string): Promise<void>;

  /** Append a reading to a meter's history and update its current value/date. */
  addReading(meterUID: string, reading: AddMeterReadingRequest): Promise<void>;

  /** Change the contract holder (owner ↔ tenant). Thin wrapper over `updateMeter`. */
  transferContract(meterUID: string, newHolder: ContractHolder): Promise<void>;

  /** Attach a document (photo, contract, certificate) to a meter. */
  addDocument(meterUID: string, document: Omit<MeterDocument, 'id' | 'uploadDate'>): Promise<void>;

  /** Upload a photo file to Storage and attach it to the meter as a `photo` document. */
  addPhoto(meterUID: string, file: File): Promise<void>;

  /** Upload any file to Storage and attach it to the meter as a document of `type`. */
  addFile(meterUID: string, file: File, type: MeterDocument['type']): Promise<void>;

  /** Remove a document from a meter by id. */
  removeDocument(meterUID: string, documentId: string): Promise<void>;

  /**
   * Check whether an EAN code is unique within the current property's meters.
   * Returns `true` if unique (or if `eanCode` is blank). `excludeMeterUID`
   * lets the edit dialog exclude the meter being edited.
   */
  isEanCodeUnique(eanCode: string, excludeMeterUID?: string): boolean;

  /** Synchronous lookup helper. Returns `null` if not loaded. */
  getMeterById(meterUID: string): Meter | null;
}

export const METERS_STORE = new InjectionToken<IMetersStore>('METERS_STORE');

export type MetersStoreMode = 'mock' | 'firestore';

/**
 * Reads the desired store mode from the URL query string
 * (`?store=firestore|mock`) with `localStorage.meters.storeMode` as a sticky
 * fallback. Defaults to `firestore` — meters are persisted user data, so
 * production traffic should hit the real backend. Demos and Storybook opt
 * into mock explicitly.
 */
export function resolveMetersStoreMode(): MetersStoreMode {
  if (typeof window === 'undefined') return 'firestore';
  if (environment.production) return 'firestore'; // QA-only mock store: never honour ?store=mock in production (silent data loss).
  const url = new URL(window.location.href);
  const param = url.searchParams.get('store');
  if (param === 'firestore' || param === 'mock') {
    try {
      window.localStorage.setItem('meters.storeMode', param);
    } catch {
      /* storage may be unavailable; ignore */
    }
    return param;
  }
  try {
    const stored = window.localStorage.getItem('meters.storeMode');
    if (stored === 'firestore' || stored === 'mock') return stored;
  } catch {
    /* ignore */
  }
  return 'firestore';
}
